import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';

export interface LocalAddressCity {
  cityMunProvinceId: number;
  cityMunCode: string;
  provinceCode: string;
  class: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
}

interface UseLocalAddressesCitiesReturn {
  cities: LocalAddressCity[];
  cityOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: (provinceCode: string) => Promise<void>;
}

export const useLocalAddressesCities = (provinceCode?: string): UseLocalAddressesCitiesReturn => {
  const [cities, setCities] = useState<LocalAddressCity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCities = useCallback(async (province: string) => {
    if (!province) {
      setCities([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const encodedProvince = encodeURIComponent(province);
      
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        cities: LocalAddressCity[];
      }>(
        `/api/local-addresses/cities?province=${encodedProvince}`,
        {},
        15 * 60 * 1000 // 15 minutes TTL - cities change less frequently
      );

      if (response.success && response.cities) {
        const sortedCities = [...response.cities].sort((a, b) =>
          a.cityMunCode.localeCompare(b.cityMunCode)
        );

        console.log("Fetched cities (cached/sorted):", sortedCities);

        setCities(sortedCities);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
      setError('Failed to fetch cities');
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (provinceCode) {
      fetchCities(provinceCode);
    } else {
      setCities([]);
    }
  }, [provinceCode, fetchCities]);

  // Convert cities to location options format for dropdowns
  const cityOptions = cities.map(city => city.cityMunCode);

  return {
    cities,
    cityOptions,
    loading,
    error,
    refetch: fetchCities
  };
};
